import { useCallback } from "react";

/**
 * VariableInserter — chips clicáveis que inserem uma variável de template
 * na posição atual do cursor da textarea vinculada.
 *
 * v0.99.5 · Fev/2026 · Componente reutilizável para todos os fluxos de
 * mensagem (Campanhas, Envio em massa, Regras de automação, Pós-atendimento,
 * Recepção/Upsell, Mensagens transacionais).
 *
 * Uso:
 *
 *   const textareaRef = useRef(null);
 *   const [msg, setMsg] = useState("");
 *   ...
 *   <textarea ref={textareaRef} value={msg} onChange={(e) => setMsg(e.target.value)} />
 *   <VariableInserter
 *     textareaRef={textareaRef}
 *     value={msg}
 *     onChange={setMsg}
 *     variables={[
 *       { token: "{nome_cliente}", label: "Primeiro nome do cliente" },
 *       { token: "{veiculo}", label: "Veículo/produto do cliente" },
 *     ]}
 *   />
 *
 * Se `textareaRef.current` não existir (ou o cursor não estiver focado),
 * o token é acrescentado no fim do valor atual — nunca perde click.
 */
export default function VariableInserter({
  textareaRef, value, onChange, variables,
  label = "Variáveis:", className = "",
}) {
  const insert = useCallback((token) => {
    const ta = textareaRef?.current;
    // Fallback quando não há ref ou textarea desmontada: append.
    if (!ta) {
      onChange((value || "") + token);
      return;
    }
    const currentValue = ta.value ?? value ?? "";
    const start = typeof ta.selectionStart === "number" ? ta.selectionStart : currentValue.length;
    const end   = typeof ta.selectionEnd === "number"   ? ta.selectionEnd   : currentValue.length;
    const next = currentValue.slice(0, start) + token + currentValue.slice(end);
    onChange(next);
    // Repositiona cursor logo após o token inserido no próximo tick
    // (aguarda o re-render controlado pelo onChange).
    requestAnimationFrame(() => {
      try {
        ta.focus();
        const pos = start + token.length;
        ta.setSelectionRange(pos, pos);
      } catch (_) {
        // JSDom/older browsers — ignora silenciosamente.
      }
    });
  }, [textareaRef, value, onChange]);

  if (!variables || variables.length === 0) return null;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="variable-inserter">
      {label && (
        <span className="text-[11px] text-slate-500 tracking-tight mr-0.5 select-none">
          {label}
        </span>
      )}
      {variables.map((v) => (
        <button
          key={v.token}
          type="button"
          onClick={() => insert(v.token)}
          data-testid={`insert-var-${v.token.replace(/[{}]/g, "")}`}
          title={v.label ? `Inserir ${v.token} — ${v.label}` : `Inserir ${v.token}`}
          className="inline-flex items-center font-mono text-[11px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-900 hover:text-white text-slate-700 border border-slate-200 hover:border-slate-900 transition-colors cursor-pointer"
        >
          {v.token}
        </button>
      ))}
    </div>
  );
}
